/** Autosave without React (tested in autosave.test.ts; `useAutosave` in permits/data.ts wraps it).
 *
 * A value is saved `delay` ms after its last change when it differs from what the server has (compared by a
 * signature string). One save at a time: a change made while a save is in flight is saved right after it. Leaving
 * never loses the last change: `flush()` saves at once (route change, "Buy Lux"; it resolves when the server has
 * it), and `unload()` hands the unsaved value to a keepalive request when the page is going away (tab close,
 * reload, a full navigation such as Stripe Checkout), which survives the page. A failed save is retried by itself
 * when the failure is transient (network, 5xx), else on the next change. */

export type SaveState = "saved" | "pending" | "saving" | "error";

export interface Timers {
  set: (fn: () => void, ms: number) => unknown;
  clear: (handle: unknown) => void;
}

const realTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export interface AutosaverOptions<T> {
  save: (value: T) => Promise<unknown>;
  /** Sends `value` with a request that outlives the page (fetch keepalive); false when it could not be sent. */
  unload?: (value: T) => boolean;
  /** Called on every state change. */
  onState?: (state: SaveState, error: unknown) => void;
  delay?: number;
  /** Wait before retrying a transient failure. */
  retryDelay?: number;
  /** Whether a failure is worth retrying by itself (default: no HTTP status, or 5xx / 408 / 429). */
  transient?: (error: unknown) => boolean;
  timers?: Timers;
}

export function isTransient(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== "number") return true; // network error, aborted
  return status >= 500 || status === 408 || status === 429;
}

export class Autosaver<T> {
  state: SaveState = "saved";
  error: unknown = null;
  /** The signature the server has (null until the first value). */
  private saved: string | null = null;
  private latest: { value: T; sig: string } | null = null;
  private timer: unknown = null;
  private inFlight: Promise<void> | null = null;
  private inFlightSig: string | null = null;
  private waiters: (() => void)[] = [];
  private readonly o: Required<Omit<AutosaverOptions<T>, "unload" | "onState">> & Pick<AutosaverOptions<T>, "unload" | "onState">;

  constructor(options: AutosaverOptions<T>) {
    this.o = { delay: 700, retryDelay: 3000, transient: isTransient, timers: realTimers, ...options };
  }

  /** Whether the latest value is not on the server yet. */
  get dirty(): boolean {
    return this.latest != null && this.latest.sig !== this.saved;
  }

  /** The value as the server has it (the first value loaded): nothing to save. */
  init(value: T, sig: string): void {
    this.saved = sig;
    this.latest = { value, sig };
    this.set("saved", null);
  }

  /** A new value: saved after `delay` unless it is what the server has. The first value counts as loaded. */
  update(value: T, sig: string): void {
    if (this.saved == null) return this.init(value, sig);
    this.latest = { value, sig };
    this.clearTimer();
    if (sig === this.saved && !this.inFlight) return this.set("saved", null);
    if (sig === this.inFlightSig) return; // already being saved
    this.set("pending", null);
    this.timer = this.o.timers.set(() => {
      this.timer = null;
      void this.run();
    }, this.o.delay);
  }

  /** Saves the latest value now (no wait); resolves once the server has it or the save failed. */
  flush(): Promise<void> {
    this.clearTimer();
    if (!this.dirty) return this.inFlight ?? Promise.resolve();
    return this.run();
  }

  /** Saves the latest value now and rejects when the server does not have it afterwards (a failed save): for
   * leaving the page on purpose (Buy Lux), which must not go with unsaved work. */
  async flushOrThrow(): Promise<void> {
    await this.flush();
    if (this.dirty && this.state === "error") throw this.error instanceof Error ? this.error : new Error("The change could not be saved.");
  }

  /** The page is going away: hands an unsaved value to the keepalive request. Returns false when a change could
   * not be handed over (no unload sender, or it failed), so the caller can ask the browser to warn. */
  unload(): boolean {
    this.clearTimer();
    if (!this.dirty || !this.latest) return true;
    if (!this.o.unload) return false;
    const sent = this.o.unload(this.latest.value);
    if (sent) {
      this.saved = this.latest.sig;
      this.set("saved", null);
    }
    return sent;
  }

  /** Stops the timers (the component is gone); call `flush()` first to keep the last change. */
  dispose(): void {
    this.clearTimer();
  }

  private run(): Promise<void> {
    if (this.inFlight) {
      // one save at a time: the latest value goes right after the one in flight
      return new Promise<void>((resolve) => this.waiters.push(resolve));
    }
    const target = this.latest;
    if (!target || target.sig === this.saved) return Promise.resolve();
    this.inFlightSig = target.sig;
    this.set("saving", null);
    const done = this.o
      .save(target.value)
      .then(
        () => {
          this.saved = target.sig;
          this.finish(null);
        },
        (error: unknown) => this.finish(error),
      );
    this.inFlight = done;
    return done;
  }

  private finish(error: unknown): void {
    this.inFlight = null;
    this.inFlightSig = null;
    const waiters = this.waiters.splice(0);
    if (error != null) {
      this.set("error", error);
      if (this.o.transient(error)) {
        this.clearTimer();
        this.timer = this.o.timers.set(() => {
          this.timer = null;
          void this.run();
        }, this.o.retryDelay);
      }
      waiters.forEach((w) => w());
      return;
    }
    if (this.dirty) {
      // changed while saving: save the newer value now (or after the debounce when a timer waits)
      if (this.timer == null) void this.run().then(() => waiters.forEach((w) => w()));
      else {
        this.set("pending", null);
        waiters.forEach((w) => w());
      }
      return;
    }
    this.set("saved", null);
    waiters.forEach((w) => w());
  }

  private clearTimer(): void {
    if (this.timer != null) this.o.timers.clear(this.timer);
    this.timer = null;
  }

  private set(state: SaveState, error: unknown): void {
    const changed = state !== this.state || error !== this.error;
    this.state = state;
    this.error = error;
    if (changed) this.o.onState?.(state, error);
  }
}

/** One indicator for several autosaves: a failure first, then saving, then unsaved, else saved. */
export function combineSaveStates(states: readonly (SaveState | null | undefined)[]): SaveState {
  const s = states.filter((x): x is SaveState => !!x);
  if (s.includes("error")) return "error";
  if (s.includes("saving")) return "saving";
  if (s.includes("pending")) return "pending";
  return "saved";
}

/** The indicator's words (the Sketch editor's: "All changes saved", "Saving…", "Unsaved changes"). */
export function saveStateText(state: SaveState): string {
  return state === "saved" ? "All changes saved" : state === "saving" ? "Saving…" : state === "pending" ? "Unsaved changes" : "Not saved";
}
